import React, { useEffect, useState } from 'react';
import { customerAPI } from '../services/api';
import { formatDateTime } from '../utils/helpers';
import toast from 'react-hot-toast';

function CustomerModal({ customer, onClose, onSaved }) {
  const [form, setForm] = useState(customer || { name: '', phone: '', email: '', address: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (customer) {
        await customerAPI.update(customer.id, form);
        toast.success('Customer updated!');
      } else {
        await customerAPI.create(form);
        toast.success('Customer created!');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{customer ? 'Edit Customer' : 'New Customer'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Rahim Ahmed" required />
          </div>
          <div className="form-group">
            <label className="form-label">Phone *</label>
            <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+8801700000000" required />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="rahim@gmail.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea className="form-input" value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Dhaka, Bangladesh" rows={3} style={{ resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : null}
              {customer ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | customer obj
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    customerAPI.getAll()
      .then(res => setCustomers(res.data.data || []))
      .catch(() => toast.error('Failed to load customers'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this customer?')) return;
    try {
      await customerAPI.deactivate(id);
      toast.success('Customer deactivated');
      load();
    } catch { toast.error('Failed'); }
  };

  const filtered = customers.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search) ||
    c.customerCode?.includes(search)
  );

  return (
    <div className="animate-fade">
      <div className="page-header">
        <h1 className="page-title">Customers</h1>
        <p className="page-subtitle">{customers.length} total customers</p>
      </div>

      <div className="action-bar">
        <input
          className="form-input"
          style={{ maxWidth: 280 }}
          placeholder="Search by name, phone, code..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button className="btn btn-primary" onClick={() => setModal('create')}>+ New Customer</button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Address</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8}><div className="empty-state"><div className="empty-state-icon">◉</div><div className="empty-state-text">No customers found</div></div></td></tr>
            ) : filtered.map(c => (
              <tr key={c.id}>
                <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}>{c.customerCode}</span></td>
                <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{c.name}</td>
                <td>{c.phone}</td>
                <td>{c.email || '—'}</td>
                <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.address || '—'}</td>
                <td><span className={`badge ${c.isActive ? 'badge-green' : 'badge-red'}`}>{c.isActive ? 'Active' : 'Inactive'}</span></td>
                <td style={{ fontSize: 12 }}>{formatDateTime(c.createdAt)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setModal(c)}>Edit</button>
                    {c.isActive && <button className="btn btn-danger btn-sm" onClick={() => handleDeactivate(c.id)}>Deactivate</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <CustomerModal
          customer={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
